import { useRef, type ReactNode } from "react";
import {
  Telescope,
  Link2,
  ArrowLeftRight,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { buildAgentMention, getHostLabel } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is the 360 Inspector?                                       */
/* -------------------------------------------------------------------------- */

export function WhatIsInspectorStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What does the 360 Inspector do?">
        <p className="text-sm leading-7 text-muted-foreground">
          The FogBugz 360 Inspector gives you a{" "}
          <strong className="text-foreground/90">unified view</strong> across both FogBugz and
          SpecTree. Give it either a FogBugz case ID or a SpecTree epic identifier, and it
          fetches both sides of the linkage — producing a single report with status, owner,
          links, recent activity, and blockers from both systems.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          {"It's"} a read-only inspector — it never changes anything in either system.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Two directions">
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={ArrowLeftRight}
            title="FogBugz to SpecTree"
            description="Give it a FogBugz case ID and it finds the linked Epic Request or epic in SpecTree, including progress summary."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={ArrowLeftRight}
            title="SpecTree to FogBugz"
            description="Give it a SpecTree epic identifier and it traces back to the originating FogBugz case, if one exists."
            accentClass="text-blue-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Linkage health check">
        <p className="text-sm leading-7 text-muted-foreground">
          The Inspector checks whether the cross-system links are intact in both directions.
          It looks for:
        </p>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-400" />
            <strong className="text-foreground/80">[SpecTree-Link] markers</strong> — written in FogBugz case events when the Bridge creates an ER
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-400" />
            <strong className="text-foreground/80">originatingTicketUrl</strong> — the FogBugz URL stored on the SpecTree Epic Request
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-400" />
            If only one direction has a link, the report flags it as a{" "}
            <strong className="text-foreground/80">one-way linkage warning</strong>
          </p>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Run the Inspector                                                */
/* -------------------------------------------------------------------------- */

export function RunInspectorStep() {
  const host = useResolvedHost();
  const inspectorMention = buildAgentMention("fogbugz-360-inspector", host);
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="With a FogBugz case ID">
        <p className="text-sm leading-7 text-muted-foreground">
          If you have a FogBugz case and want to see its SpecTree counterpart:
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText={`${inspectorMention} Inspect case 1234`}>
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-amber-400">{inspectorMention}</span>
            <span className="text-foreground/70">{" "}Inspect case 1234</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="With a SpecTree epic ID">
        <p className="text-sm leading-7 text-muted-foreground">
          If you have a SpecTree epic and want to find the originating FogBugz case:
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText={`${inspectorMention} Inspect ENG-E175`}>
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-amber-400">{inspectorMention}</span>
            <span className="text-foreground/70">{" "}Inspect ENG-E175</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Read the report">
        <p className="text-sm leading-7 text-muted-foreground">
          The Inspector produces a side-by-side table with data from both systems:
        </p>
        <CopyableTerminal title={`${getHostLabel(host)} — 360 Report`}>
          <div className="space-y-1.5">
            <p className="text-blue-400">FogBugz {"<->"} SpecTree 360 Report</p>
            <p className="text-muted-foreground/70">| Field    | FogBugz          | SpecTree              |</p>
            <p className="text-muted-foreground/70">|----------|------------------|-----------------------|</p>
            <p className="text-foreground/80">| Status   | Active           | In Progress (3/8)     |</p>
            <p className="text-foreground/80">| Owner    | jdoe             | Engineering / @jdoe   |</p>
            <p className="text-foreground/80">| Blockers | none             | 1 blocked feature     |</p>
          </div>
        </CopyableTerminal>
        <p className="mt-3 text-sm leading-7 text-muted-foreground">
          The report also includes a{" "}
          <strong className="text-foreground/90">Linkage health</strong> section and{" "}
          <strong className="text-foreground/90">Recent activity</strong> from both systems
          so you can quickly assess whether things are in sync.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={Link2}
            title="Linkage health"
            description="Confirms both systems point at each other. Flags one-way links or missing connections."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={Activity}
            title="Recent activity"
            description="Shows the most recent events from both FogBugz and SpecTree with ISO timestamps."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={Telescope}
            title="Read-only"
            description="The Inspector never writes to either system. It is purely an observation and reporting tool."
            accentClass="text-blue-400"
          />
        </div>
      </NumberedInstructionLast>
    </div>
  );
}
